import PanToolIcon from "@mui/icons-material/PanTool";
import Typography from "@mui/material/Typography";
import { ProgressButton, useToast } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import { selectHandRaisedForWorkstation } from "~/features/andon/andon.selectors";
import { useSupportBundle } from "~/features/supportBundle/useSupportBundle";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useToggleHandRaiseMutation } from "~/redux/warehouse/andon.hooks";

import { RequestTypeModal, RequestType } from "./RequestTypeModal";

export const RaiseHandButton = () => {
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const { isSupportBundleEnabled } = useFlag();
  const clientConfig = useAppSelector(selectServerConfig);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const workstationId = siteWorkstation?.id;
  const autostoreGridId = siteWorkstation?.autostoreGridId;
  const handRaised = useAppSelector(
    selectHandRaisedForWorkstation(workstationId)
  );

  const [toggleHandRaise, { isLoading: isRaisingHand }] =
    useToggleHandRaiseMutation();

  const { sendSupportBundle, isSendingSupportBundle } = useSupportBundle();

  const handleClick = () => {
    if (handRaised?.raised) {
      void handleLowerHand();
    } else if (clientConfig?.an_isHandRaiseTypePickerEnabled) {
      setIsModalOpen(true);
    } else {
      void handleRaiseHand("general");
    }
  };

  const handleLowerHand = async () => {
    if (!autostoreGridId || !workstationId) {
      return errorToast("Missing workstation information");
    }
    try {
      await toggleHandRaise({
        autostoreGridId,
        workstationId,
        raiseHand: false
      }).unwrap();
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  const handleRaiseHand = async (requestType: RequestType) => {
    if (!autostoreGridId || !workstationId) {
      return errorToast("Missing workstation information");
    }
    try {
      await toggleHandRaise({
        autostoreGridId,
        workstationId,
        raiseHand: true,
        requestType
      }).unwrap();
      setIsModalOpen(false);
      if (isSupportBundleEnabled) {
        await sendSupportBundle();
      }
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  return (
    <>
      <ProgressButton
        emphasis="high"
        responsive
        variant="contained"
        color={handRaised?.raised ? "info" : "inherit"}
        startIcon={
          <PanToolIcon
            sx={{
              color: "warning.dark"
            }}
          />
        }
        loading={isRaisingHand || isSendingSupportBundle}
        onClick={handleClick}
      >
        <Typography sx={{ minWidth: 100 }}>
          {handRaised?.raised ? t("lower hand") : t("raise hand")}
        </Typography>
      </ProgressButton>

      <RequestTypeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleRaiseHand}
        isLoading={isRaisingHand || isSendingSupportBundle}
      />
    </>
  );
};
